import { Grid, Select, Textarea, TextInput } from "@mantine/core";
import { regionApi } from "apis";
import { CreatableSelect } from "components";
import { PARTNER_TYPE_OPTIONS } from "constants/common/partner";
import { PartnerCreateBodyType } from "constants/types/partner";
import { RegionResponse } from "constants/types/region";
import { FormikProps } from "formik";
import { FC } from "react";
import { getInitials } from "utils/helpers/string";
import { useNotification } from "utils/hooks";
import { useUsers } from "utils/hooks/request/useUsers";

interface SelectOption {
  label: string;
  value: string;
}

interface PartnerFormFieldsProps {
  formik: FormikProps<PartnerCreateBodyType>;
  regionOptions: SelectOption[];
  onRegionMutate: () => void;
}

const spanHalf = { base: 12, md: 6 };

const PartnerFormFields: FC<PartnerFormFieldsProps> = ({
  formik,
  regionOptions,
  onRegionMutate,
}) => {
  const { notification } = useNotification();
  const { userOptions } = useUsers();

  const getFieldError = (field: keyof PartnerCreateBodyType) =>
    formik.touched[field] && formik.errors[field]
      ? (formik.errors[field] as string)
      : null;

  return (
    <Grid>
      <Grid.Col span={spanHalf}>
        <TextInput
          label="Mã đối tác"
          placeholder="VD: KH-001"
          {...formik.getFieldProps("code")}
          error={getFieldError("code")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <TextInput
          label="Tên nhà cung cấp"
          placeholder="Nhập tên nhà cung cấp"
          withAsterisk
          {...formik.getFieldProps("name")}
          error={getFieldError("name")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <Select
          label="Loại đối tác"
          placeholder="Chọn loại đối tác"
          withAsterisk
          data={PARTNER_TYPE_OPTIONS}
          value={formik.values.type}
          onChange={(value) => formik.setFieldValue("type", value)}
          onBlur={() => formik.setFieldTouched("type", true)}
          error={getFieldError("type")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <CreatableSelect
          label="Địa bàn"
          placeholder="Chọn địa bàn"
          withAsterisk
          data={regionOptions}
          value={formik.values.regionId}
          onChange={(value) => formik.setFieldValue("regionId", value)}
          onBlur={() => formik.setFieldTouched("regionId", true)}
          error={getFieldError("regionId")}
          onCreate={async (query) => {
            try {
              const res = await regionApi.create({
                name: query,
                code: getInitials(query),
              });
              const data: RegionResponse = res.data.data;
              notification.success({
                title: `Tạo địa bàn "${query}" thành công`,
              });
              onRegionMutate();
              return { label: data.name, value: data.id };
            } catch (error: any) {
              notification.error({
                title: "Tạo địa bàn thất bại",
                message: error?.error?.message,
              });
              throw error;
            }
          }}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <TextInput
          label="Mã số thuế"
          placeholder="Nhập mã số thuế"
          withAsterisk
          {...formik.getFieldProps("taxCode")}
          error={getFieldError("taxCode")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <TextInput
          label="Số điện thoại"
          placeholder="Nhập số điện thoại"
          {...formik.getFieldProps("phone")}
          error={getFieldError("phone")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <CreatableSelect
          label="Người phụ trách"
          placeholder="Chọn người phụ trách"
          allowCreate={false}
          data={userOptions ?? []}
          value={formik.values.contactPersonId || null}
          onChange={(value) =>
            formik.setFieldValue("contactPersonId", value ?? "")
          }
          onBlur={() => formik.setFieldTouched("contactPersonId", true)}
          error={getFieldError("contactPersonId")}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Địa chỉ"
          placeholder="Nhập địa chỉ (in trên phiếu giao nhận)"
          autosize
          minRows={1}
          {...formik.getFieldProps("address")}
          error={getFieldError("address")}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Ghi chú"
          placeholder="Nhập ghi chú (tuỳ chọn)"
          autosize
          minRows={2}
          {...formik.getFieldProps("notes")}
          error={getFieldError("notes")}
        />
      </Grid.Col>
    </Grid>
  );
};

export default PartnerFormFields;
